import styled from 'styled-components';

import { colors } from 'constants/colors';

import { dateFromString } from 'helpers/dateTimes';

import {
    HotDrinksInstructionFragmentFragment,
    MicrostoreColumnWithShelvesFragmentFragment,
    MicrostoreColumnInventoryFragmentFragment,
    MicrostoreColumnSetupFragmentFragment,
    RemoveProductsInstructionFragmentFragment,
} from 'data/__generated__';

export const InventoryColumnDisplay = ({
    column,
    columnInventory,
    columnSetup,
    setSelectedLocationId,
    selectedLocationId,
    weekdateBeforeNextSetup,
    removedProducts,
    hotDrinksInstruction,
}: {
    column: MicrostoreColumnWithShelvesFragmentFragment;
    columnInventory: MicrostoreColumnInventoryFragmentFragment;
    columnSetup: MicrostoreColumnSetupFragmentFragment;
    setSelectedLocationId?: (selectedLocationId: string | null) => void;
    selectedLocationId?: string | null;
    weekdateBeforeNextSetup: Date;
    removedProducts?: RemoveProductsInstructionFragmentFragment['removedProducts'];
    hotDrinksInstruction?: HotDrinksInstructionFragmentFragment;
}) => {
    const getRemovedProductIdForLocation = (locationId: string): string | null => {
        const match = removedProducts?.find((rp) => rp.microstoreLocationIds?.includes(locationId));
        return match?.productId ?? null;
    };

    const getRemovedProductQuantityForLocation = (locationId: string): number | string | null => {
        const match = removedProducts?.find((rp) => rp.microstoreLocationIds?.includes(locationId));
        if (!match?.quantityDetails) return null;
        const isFirstLocation = match.microstoreLocationIds[0] === locationId;
        if (!isFirstLocation) return '-';
        return match.quantityDetails.reduce((acc, { quantity }) => acc + quantity, 0);
    };

    const getHotDrinksTargetForLocation = (locationId: string): number | null => {
        const match = hotDrinksInstruction?.locations?.find((loc) => loc.microstoreLocationId === locationId);
        if (!match) return null;
        return match.targetTrayLevel;
    };

    const selectedLocationInventoryInfo = selectedLocationId
        ? columnInventory.checkedLocations.find(
              ({ microstoreLocationId }) => microstoreLocationId === selectedLocationId,
          )
        : undefined;

    const selectedLocationProductId = (() => {
        if (!selectedLocationId) return null;
        const removedProductId = getRemovedProductIdForLocation(selectedLocationId);
        if (removedProductId) return removedProductId;
        if (selectedLocationInventoryInfo?.productId) return selectedLocationInventoryInfo.productId;
    })();

    return (
        <>
            <Column>
                {column.shelves.map((shelf) => (
                    <Shelf key={shelf._id} $numberOfLocationRow={shelf.locationTable.length}>
                        {shelf.locationTable.map((locationRow, rowIndex) => (
                            <LocationRow key={rowIndex} $isFirstRow={rowIndex === 0}>
                                {locationRow.map((location) => {
                                    const locationInventoryInfo = columnInventory.checkedLocations.find(
                                        ({ microstoreLocationId }) => microstoreLocationId === location._id,
                                    );

                                    const totalInventoryQuantity =
                                        locationInventoryInfo?.quantityDetails?.reduce(
                                            (acc, { quantity }) => acc + quantity,
                                            0,
                                        ) ?? null;

                                    const totalExpired =
                                        locationInventoryInfo?.quantityDetails?.reduce(
                                            (acc, { expiryDate, quantity }) => {
                                                const isExpired =
                                                    expiryDate && dateFromString(expiryDate) < weekdateBeforeNextSetup;
                                                return acc + (isExpired ? quantity : 0);
                                            },
                                            0,
                                        ) ?? null;

                                    const removalMatch = !!getRemovedProductIdForLocation(location._id);
                                    const removedQuantity = getRemovedProductQuantityForLocation(location._id);

                                    const hotDrinksTarget = getHotDrinksTargetForLocation(location._id);
                                    const hasHotDrinks = hotDrinksTarget !== null;

                                    const displayQuantity = hasHotDrinks
                                        ? `${hotDrinksTarget}g`
                                        : removalMatch
                                          ? removedQuantity
                                          : totalInventoryQuantity;

                                    const locationProductId =
                                        getRemovedProductIdForLocation(location._id) ??
                                        locationInventoryInfo?.productId ??
                                        location.productId;
                                    const isZeroInventoryProduct =
                                        !!locationProductId &&
                                        !!columnSetup.postSiteSetupZeroInventoryProductIds?.includes(locationProductId);

                                    return (
                                        <Location
                                            key={location._id}
                                            onClick={() => {
                                                setSelectedLocationId?.(location._id);
                                            }}
                                            $isSelected={
                                                selectedLocationId === location._id ||
                                                (!!selectedLocationProductId &&
                                                    locationProductId === selectedLocationProductId)
                                            }
                                            $hasRemovedProducts={removalMatch}
                                            $hasZeroInventoryProduct={isZeroInventoryProduct}
                                            $hasHotDrinks={hasHotDrinks}
                                        >
                                            <LocationText>{displayQuantity}</LocationText>
                                            {displayQuantity && totalExpired ? (
                                                <LocationText $color={colors.red}>({totalExpired})</LocationText>
                                            ) : null}
                                        </Location>
                                    );
                                })}
                            </LocationRow>
                        ))}
                    </Shelf>
                ))}
            </Column>
            <LegendsContainer>
                <Legend>
                    <LegendSymbol>
                        <Location $isSelected={false}>
                            <LocationText>4</LocationText>
                            <LocationText $color={colors.red}>(2)</LocationText>
                        </Location>
                    </LegendSymbol>
                    <LegendText>L'emplacement possède 4 produits dont 2 périmés</LegendText>
                </Legend>
                <Legend>
                    <LegendSymbol>
                        <Location $isSelected>
                            <LocationText>4</LocationText>
                            <LocationText $color={colors.red}>(2)</LocationText>
                        </Location>
                    </LegendSymbol>
                    <LegendText>Emplacement sélectionné</LegendText>
                </Legend>
                <Legend />
                {removedProducts && removedProducts.length > 0 && (
                    <Legend>
                        <LegendSymbol>
                            <Location $isSelected={false} $hasRemovedProducts>
                                <LocationText>4</LocationText>
                            </Location>
                        </LegendSymbol>
                        <LegendText>Emplacement avec 4 produits retirés</LegendText>
                    </Legend>
                )}
                {columnSetup.postSiteSetupZeroInventoryProductIds &&
                    columnSetup.postSiteSetupZeroInventoryProductIds.length > 0 && (
                        <Legend>
                            <LegendSymbol>
                                <Location $isSelected={false} $hasZeroInventoryProduct>
                                    <LocationText>0</LocationText>
                                </Location>
                            </LegendSymbol>
                            <LegendText>Inventaire zéro manuel</LegendText>
                        </Legend>
                    )}
                {hotDrinksInstruction?.locations && hotDrinksInstruction.locations.length > 0 && (
                    <Legend>
                        <LegendSymbol>
                            <Location $isSelected={false} $hasHotDrinks>
                                <LocationText>500g</LocationText>
                            </Location>
                        </LegendSymbol>
                        <LegendText>Bac machine à café (grammes)</LegendText>
                    </Legend>
                )}
            </LegendsContainer>
        </>
    );
};

const Column = styled.div`
    border-radius: 5px;
    padding: 5px;
    background-color: ${colors.pureWhite};
    width: 100%;
`;

const Shelf = styled.div<{ $numberOfLocationRow: number }>`
    width: 90%;
    margin: auto;
    transform: perspective(1200px) rotateX(45deg);
    /* Because of perspective, we need to readjust the margins */
    margin-top: ${({ $numberOfLocationRow }) => 15 - $numberOfLocationRow * 7}px;
    margin-bottom: ${({ $numberOfLocationRow }) => -$numberOfLocationRow * 5}px;
`;

const LocationRow = styled.div<{ $isFirstRow: boolean }>`
    display: flex;
    flex-direction: row;
    border: 1px solid ${colors.darkGrey};
    border-top: ${({ $isFirstRow }) => ($isFirstRow ? 1 : 0)}px solid ${colors.darkGrey};
    background-color: ${colors.pureWhite};
`;

const Location = styled.div<{
    $isSelected: boolean;
    $hasRemovedProducts?: boolean;
    $hasZeroInventoryProduct?: boolean;
    $hasHotDrinks?: boolean;
}>`
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex-basis: 0;
    flex-grow: 1;
    border: ${({ $isSelected, $hasRemovedProducts, $hasZeroInventoryProduct, $hasHotDrinks }) => {
        if ($isSelected) return `2px solid ${colors.red}`;
        if ($hasZeroInventoryProduct) return `2px solid ${colors.orange}`;
        if ($hasRemovedProducts) return `2px solid ${colors.blue}`;
        if ($hasHotDrinks) return `2px solid ${colors.green}`;
        return `1px solid ${colors.darkGrey}`;
    }};
    padding: 1px;
    margin: 2px;
    height: 35px;

    & > :not(:first-child) {
        margin-left: 5px;
    }
`;

const LocationText = styled.span<{ $color?: string }>`
    font-size: 14px;
    color: ${({ $color }) => $color || colors.pureBlack};
`;

const LegendsContainer = styled.div`
    margin-top: 20px;
    padding: 10px;
`;
const Legend = styled.div`
    display: flex;
    align-items: center;
`;
const LegendSymbol = styled.div`
    width: 60px;
`;
const LegendText = styled.div`
    margin-left: 10px;
`;
